// src/components/QuickTimeCalendar.jsx

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import format from 'date-fns/format';
import { parse } from 'date-fns';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import enUS from 'date-fns/locale/en-US';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

// Setup the localizer for date-fns
const locales = {
    'en-US': enUS,
};
const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek,
    getDay,
    locales,
});

/**
 * A helper function to parse date and time strings into a Date object.
 */
const parseDateTime = (dateStr, timeStr) => {
    if (!dateStr || !timeStr) return null;
    const normalized = dateStr.replace("Sept", "Sep");
    return parse(`${normalized} ${timeStr}`, "dd-MMM-yyyy HH:mm", new Date());
};


/**
 * Processes the raw API data into an array of events for React Big Calendar.
 * Handles missed check-outs and days with no attendance records.
 */
const processApiDataForCalendar = (apiData) => {
    if (!apiData || !Array.isArray(apiData)) {
        return [];
    }

    const calendarEvents = [];

    apiData.forEach(week => {
        week.days.forEach(day => {
            // 1. Process Timesheet entries as "Taken" slots
            if (day.timesheet && day.timesheet.entries) {
                day.timesheet.entries.forEach(entry => {
                    const start = parseDateTime(day.date, entry.start_time);
                    const end = parseDateTime(day.date, entry.end_time);
                    if (start && end) {
                        calendarEvents.push({
                            title: `${entry.project} (${entry.hours})`,
                            start,
                            end,
                            resource: { type: 'taken', data: entry },
                        });
                    }
                });
            }

            // 2. Process Attendance events using a clear if/else for different scenarios
            const hasAttendanceEvents = day.attendance && day.attendance.events && day.attendance.events.length > 0;

            // console.log(day, hasAttendanceEvents);
            if (hasAttendanceEvents) {
                // *** CASE 2: ATTENDANCE EVENTS EXIST. Process them. ***
                const sortedEvents = [...day.attendance.events].sort((a, b) =>
                    a.time.localeCompare(b.time)
                );

                // Process all adjacent IN/OUT pairs
                for (let i = 0; i < sortedEvents.length - 1; i++) {
                    const currentEvent = sortedEvents[i];
                    const nextEvent = sortedEvents[i + 1];
                    if (currentEvent.type === 'IN' && nextEvent.type === 'OUT') {
                        const start = parseDateTime(day.date, currentEvent.time);
                        const end = parseDateTime(day.date, nextEvent.time);
                        if (start && end && start < end) {
                            calendarEvents.push({
                                title: 'Attendance',
                                start,
                                end,
                                resource: { type: 'available' },
                            });
                        }
                        // Skip the next event as it's already paired
                        i++;
                    }
                }

                // *** CASE 3: MISSED CHECKOUT ***
                // If the very last event of the day was an unpaired 'IN', create an 8-hour slot.
                const lastEvent = sortedEvents[sortedEvents.length - 1];
                if (lastEvent.type === 'IN') {
                    const start = parseDateTime(day.date, lastEvent.time);
                    if (start) {
                        const end = new Date(start.getTime() + 8 * 60 * 60 * 1000);
                        calendarEvents.push({
                            title: 'Attendance (8h default)',
                            start,
                            end,
                            resource: { type: 'available' },
                        });
                    }
                }
            }
        });
    });

    return calendarEvents;
};

/**
 * Custom styling for calendar events based on their type ('available' or 'taken').
 */
const eventStyleGetter = (event) => {
    let style = {
        borderRadius: '5px',
        opacity: 0.9,
        color: 'white',
        border: 'none',
        display: 'block',
        padding: '2px 5px',
        fontSize: '0.8em',
    };
    if (event.resource.type === 'available') {
        style.backgroundColor = '#28a745'; // Green for available
        style.border = '1px dashed #fff';
        style.boxShadow = 'inset 0 0 5px rgba(0,0,0,0.2)';
    } else if (event.resource.type === 'taken') {
        style.backgroundColor = '#dc3545'; // Red for taken
    }
    return { style };
};


const QuickTimeCalendar = ({ timeData, onSelectTimeSlot, date }) => {
    const calendarEvents = useMemo(() =>
            processApiDataForCalendar(timeData?.data),
        [timeData]
    );

    const handleSelectSlot = ({ start, end }) => {
        // Validation: Check if it overlaps with any "taken" timesheet entry
        const isOverlapping = calendarEvents.some(event =>
            event.resource.type === 'taken' &&
            start < event.end && end > event.start
        );
        if (isOverlapping) {
            alert("Error: The selected time overlaps with an existing timesheet entry.");
            return;
        }

        // Validation: Check if it's fully contained within an "available" attendance block
        const isWithinAvailableSlot = calendarEvents.some(event =>
            event.resource.type === 'available' &&
            start >= event.start && end <= event.end
        );
        if (!isWithinAvailableSlot) {
            alert("Potential Correction Needed: This time entry is outside your recorded IN/OUT attendance. It will be flagged for review and may require correction before salary generation.");
            return;
        }

        if (onSelectTimeSlot) {
            onSelectTimeSlot({
                start: format(start, 'HH:mm'),
                end: format(end, 'HH:mm'),
                date: format(start, 'yyyy-MM-dd')
            });
        }
    };

    return (
        <Card className="col-span-1 lg:col-span-2">
            <CardHeader>
                <CardTitle>Weekly Time Visualizer</CardTitle>
            </CardHeader>
            <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                    <span className="inline-block w-3 h-3 bg-[#dc3545] rounded-full mr-1"></span> Filled Timesheet &nbsp;
                    <span className="inline-block w-3 h-3 bg-[#28a745] rounded-full mr-1"></span> Available (From Attendance)
                    <br/>
                    Click and drag on an 'Available' slot to pre-fill the form.
                </p>
                <div style={{ height: '600px' }}>
                    <Calendar
                        localizer={localizer}
                        events={calendarEvents}
                        startAccessor="start"
                        endAccessor="end"
                        defaultView="week"
                        views={['week', 'day']}
                        selectable
                        onSelectEvent={handleSelectSlot}
                        eventPropGetter={eventStyleGetter}
                        date={date}
                        min={new Date(0, 0, 0, 6, 0, 0)} // Start day view at 6 AM
                        max={new Date(0, 0, 0, 23, 59, 0)} // End day view
                        step={15}
                        timeslots={4}
                    />
                </div>
            </CardContent>
        </Card>
    );
};

QuickTimeCalendar.propTypes = {
    timeData: PropTypes.shape({
        success: PropTypes.bool,
        data: PropTypes.array,
    }),
    onSelectTimeSlot: PropTypes.func,
    date: PropTypes.instanceOf(Date),
};

export default QuickTimeCalendar;